import React, { useState, useEffect } from 'react';
import { superAdminAPI } from '../services/api';

import { useToast } from '../hooks/useToast';
import Icon from './Icon';

function SuperAdminBroadcasts({ darkMode }) {
  const toast = useToast();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [previewRecipients, setPreviewRecipients] = useState(null);
  const [sending, setSending] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    html_body: '',
    text_body: '',
    recipient_type: 'both'
  });

  useEffect(() => {
    fetchBroadcasts();
  }, []);

  const fetchBroadcasts = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getBroadcasts();
      setBroadcasts(response.data.broadcasts || []);
    } catch (err) {
      toast.error('Failed to load broadcasts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await superAdminAPI.createBroadcast(formData);
      toast.success('Broadcast draft created');
      setShowCreateModal(false);
      setFormData({ subject: '', html_body: '', text_body: '', recipient_type: 'both' });
      fetchBroadcasts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create broadcast');
    }
  };

  const handleDelete = async (broadcastId) => {
    if (!window.confirm('Are you sure you want to delete this broadcast?')) return;
    
    try {
      await superAdminAPI.deleteBroadcast(broadcastId);
      toast.success('Broadcast deleted');
      fetchBroadcasts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete broadcast');
    }
  };

  const handlePreview = async (broadcast) => {
    try {
      const response = await superAdminAPI.previewBroadcastRecipients(broadcast.id);
      setPreviewRecipients(response.data);
      setSelectedBroadcast(broadcast);
      setShowPreviewModal(true);
    } catch (err) {
      toast.error('Failed to load recipients preview');
    }
  };

  const handleSend = async (broadcast) => {
    if (!window.confirm(`Are you sure you want to send this broadcast to all recipients? This action cannot be undone.`)) return;
    
    try {
      setSending(true);
      const response = await superAdminAPI.sendBroadcast(broadcast.id);
      toast.success(response.data.message);
      setShowPreviewModal(false);
      fetchBroadcasts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      draft: { bg: '#6b7280', text: 'white' },
      sending: { bg: '#f59e0b', text: 'white' },
      sent: { bg: '#1A2811', text: 'white' },
      failed: { bg: '#ef4444', text: 'white' }
    };
    const colors = statusColors[status] || statusColors.draft;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: colors.bg,
        color: colors.text
      }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="super-admin-broadcasts">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: darkMode ? '#fff' : '#1a1a2e' }}>Broadcast Emails</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          + New Broadcast
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : broadcasts.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          backgroundColor: darkMode ? '#1a1a2e' : '#f8f9fa',
          borderRadius: '12px'
        }}>
          <Icon name="email" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', color: darkMode ? '#fff' : '#1a1a2e' }}>No Broadcasts Yet</h3>
          <p style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Create your first broadcast email to send to users and companies.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: darkMode ? '#1a1a2e' : '#fff',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{ backgroundColor: darkMode ? '#16213e' : '#f3f4f6' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Subject</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Recipients</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Sent</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Created</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', color: darkMode ? '#a5b4fc' : '#666' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((broadcast) => (
                <tr key={broadcast.id} style={{ borderBottom: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}` }}>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#fff' : '#1a1a2e' }}>
                    {broadcast.subject}
                  </td>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#a5b4fc' : '#666' }}>
                    {broadcast.recipient_type.charAt(0).toUpperCase() + broadcast.recipient_type.slice(1)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {getStatusBadge(broadcast.status)}
                  </td>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>
                    {broadcast.sent_count > 0 ? (
                      <span>{broadcast.sent_count} / {broadcast.recipients_count}</span>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>
                    {formatDate(broadcast.created_at)}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      {broadcast.status === 'draft' && (
                        <>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handlePreview(broadcast)}
                          >
                            Preview & Send
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(broadcast.id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {broadcast.status === 'sent' && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handlePreview(broadcast)}
                        >
                          View Details
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: darkMode ? '#1a1a2e' : '#fff',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px', color: darkMode ? '#fff' : '#1a1a2e' }}>Create Broadcast Email</h3>
            
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666' }}>
                  Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                    backgroundColor: darkMode ? '#16213e' : '#fff',
                    color: darkMode ? '#fff' : '#1a1a2e'
                  }}
                  placeholder="Enter email subject"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666' }}>
                  Recipients *
                </label>
                <select
                  value={formData.recipient_type}
                  onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                    backgroundColor: darkMode ? '#16213e' : '#fff',
                    color: darkMode ? '#fff' : '#1a1a2e'
                  }}
                >
                  <option value="both">All Users & Companies</option>
                  <option value="users">Users Only (opted in)</option>
                  <option value="companies">Companies Only</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666' }}>
                  HTML Content *
                </label>
                <textarea
                  value={formData.html_body}
                  onChange={(e) => setFormData({ ...formData, html_body: e.target.value })}
                  required
                  rows={10}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                    backgroundColor: darkMode ? '#16213e' : '#fff',
                    color: darkMode ? '#fff' : '#1a1a2e',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                  placeholder="<p>Hello {{recipient_name}},</p>&#10;<p>Your message here...</p>"
                />
                <small style={{ color: darkMode ? '#6b7280' : '#9ca3af' }}>
                  Use {'{{recipient_name}}'} for recipient's name, {'{{email_subject}}'} for subject
                </small>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666' }}>
                  Plain Text (Optional)
                </label>
                <textarea
                  value={formData.text_body}
                  onChange={(e) => setFormData({ ...formData, text_body: e.target.value })}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                    backgroundColor: darkMode ? '#16213e' : '#fff',
                    color: darkMode ? '#fff' : '#1a1a2e',
                    resize: 'vertical'
                  }}
                  placeholder="Plain text version for email clients that don't support HTML"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Draft
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedBroadcast && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: darkMode ? '#1a1a2e' : '#fff',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px', color: darkMode ? '#fff' : '#1a1a2e' }}>
              {selectedBroadcast.status === 'sent' ? 'Broadcast Details' : 'Preview & Send'}
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Subject:</strong>
              <p style={{ margin: '4px 0', color: darkMode ? '#fff' : '#1a1a2e' }}>{selectedBroadcast.subject}</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Status:</strong>
              <span style={{ marginLeft: '8px' }}>{getStatusBadge(selectedBroadcast.status)}</span>
            </div>

            {selectedBroadcast.status === 'sent' && (
              <div style={{ marginBottom: '20px' }}>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Results:</strong>
                <p style={{ margin: '4px 0', color: darkMode ? '#fff' : '#1a1a2e' }}>
                  {selectedBroadcast.sent_count} sent, {selectedBroadcast.failed_count} failed
                  (out of {selectedBroadcast.recipients_count} recipients)
                </p>
                <p style={{ margin: '4px 0', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>
                  Sent at: {formatDate(selectedBroadcast.sent_at)}
                </p>
              </div>
            )}

            {previewRecipients && selectedBroadcast.status !== 'sent' && (
              <div style={{ marginBottom: '20px' }}>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>
                  Recipients ({previewRecipients.total_count}):
                </strong>
                <div style={{
                  marginTop: '8px',
                  maxHeight: '200px',
                  overflow: 'auto',
                  backgroundColor: darkMode ? '#16213e' : '#f3f4f6',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  {previewRecipients.recipients.slice(0, 50).map((r, idx) => (
                    <div key={idx} style={{
                      padding: '6px 0',
                      borderBottom: idx < 49 ? `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}` : 'none',
                      fontSize: '13px',
                      color: darkMode ? '#fff' : '#1a1a2e'
                    }}>
                      <span style={{ 
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: r.type === 'user' ? '#3b82f6' : '#1A2811',
                        color: 'white',
                        fontSize: '10px',
                        marginRight: '8px'
                      }}>
                        {r.type}
                      </span>
                      {r.name} ({r.email})
                    </div>
                  ))}
                  {previewRecipients.total_count > 50 && (
                    <div style={{ padding: '8px 0', color: darkMode ? '#a5b4fc' : '#666', fontStyle: 'italic' }}>
                      ... and {previewRecipients.total_count - 50} more recipients
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>HTML Preview:</strong>
              <div style={{
                marginTop: '8px',
                padding: '16px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                maxHeight: '300px',
                overflow: 'auto'
              }} dangerouslySetInnerHTML={{ __html: selectedBroadcast.html_body }} />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedBroadcast(null);
                  setPreviewRecipients(null);
                }}
              >
                Close
              </button>
              {selectedBroadcast.status === 'draft' && previewRecipients && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleSend(selectedBroadcast)}
                  disabled={sending || previewRecipients.total_count === 0}
                >
                  {sending ? 'Sending...' : `Send to ${previewRecipients.total_count} Recipients`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminBroadcasts;

